
import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Users, 
  MessageSquare, 
  BookOpen, 
  Settings, 
  LogOut,
  Award,
  Gift,
  Trophy,
  Star,
  Menu,
  X,
  Home,
  ArrowLeft
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from '@/components/ui/drawer';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signOut, user } = useAuth();
  const isMobile = useIsMobile();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  console.log('AdminLayout render:', { 
    pathname: location.pathname, 
    userId: user?.id 
  });

  const navigation = [
    { name: '儀表板', href: '/jay/dashboard', icon: LayoutDashboard },
    { name: '會員管理', href: '/jay/users', icon: Users },
    { name: '評論管理', href: '/jay/reviews', icon: MessageSquare },
    { name: '指南管理', href: '/jay/guides', icon: BookOpen },
    { name: '會員等級', href: '/jay/levels', icon: Trophy },
    { name: '勳章管理', href: '/jay/badges', icon: Award },
    { name: '獎勵商品', href: '/jay/rewards', icon: Gift },
    { name: '兌換記錄', href: '/jay/redemptions', icon: Star },
    { name: '積分設定', href: '/jay/points-settings', icon: Settings },
    { name: '系統設定', href: '/jay/settings', icon: Settings },
  ];

  const handleLogout = async () => {
    try {
      await signOut();
      toast({
        title: '登出成功',
        description: '已成功登出管理後台',
      });
      navigate('/jay'); // 登出後回到後台登入頁面
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        variant: 'destructive',
        title: '登出失敗',
        description: '登出時發生錯誤，請重試',
      });
    }
  };

  const NavigationContent = () => (
    <>
      <div className="p-4 md:p-6">
        <h1 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">
          TruStay 後台
        </h1>
        {user && (
          <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
            歡迎，管理員
          </p>
        )}
      </div>
      
      {/* 回到前台按鈕 */}
      <div className="px-4 md:px-6 pb-4">
        <Button
          variant="outline"
          size={isMobile ? "sm" : "default"}
          onClick={() => navigate('/')}
          className="flex items-center w-full"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          回到前台
        </Button>
      </div>
      
      <nav className="mt-2 md:mt-6 flex-1">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setIsDrawerOpen(false)}
              className={`flex items-center px-4 md:px-6 py-3 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-r-2 border-blue-600'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <item.icon className="mr-3 h-4 w-4 md:h-5 md:w-5" />
              <span className="text-sm md:text-base">{item.name}</span>
            </Link>
          );
        })}
      </nav>
      <div className="p-4 md:p-6 mt-auto">
        <Button
          variant="outline"
          size={isMobile ? "sm" : "default"}
          onClick={handleLogout}
          className="flex items-center w-full"
        >
          <LogOut className="mr-2 h-4 w-4" />
          登出
        </Button>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* 手機版頂部導航 */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border-b">
          <div className="flex items-center justify-between p-4">
            <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
              <DrawerTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Menu className="h-5 w-5" />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="h-[85vh]">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-lg font-semibold">導航選單</h2>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setIsDrawerOpen(false)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <NavigationContent />
                </div>
              </DrawerContent>
            </Drawer>
            
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              TruStay 後台
            </h1>
            
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <Home className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* 主內容區 */}
        <main className="p-4">
          <Outlet />
        </main>
      </div>
    );
  }

  // 桌面版布局
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex">
        {/* 側邊欄 */}
        <div className="w-64 bg-white dark:bg-gray-800 shadow-lg flex flex-col">
          <NavigationContent />
        </div>

        {/* 主內容區 */}
        <div className="flex-1">
          <main className="p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
